import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { useQsFilters } from '../../hooks/filters'
import { Regista } from '../../types'
import {
  useDeleteRegista,
  useRegisti,
  useUnificaRegisti,
} from '../../hooks/registi'
import { Field, Formik } from 'formik'
import { MultiSelectField, ReactSelectField } from '../../components/form'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? '',
})

export default function RegistiList() {
  const { filters, uiFilters, setFiltersDebounced, setFilters } =
    useQsFilters(initFilters)
  const { data } = useRegisti(filters)
  const deleteRegista = useDeleteRegista()
  const [modalRm, modalRmActions] = useModalTrigger<Regista>()
  const [modalUnifica, modalUnificaActions] = useModalTrigger()

  const filtersRegisti = {
    page_size: 1000,
  }

  const { data: registiFull } = useRegisti(filtersRegisti)

  const registiOptions =
    registiFull?.results.map((regista) => ({
      value: regista.id,
      label: regista.nome,
    })) ?? []

  const unificaRegisti = useUnificaRegisti()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Registi</h2>
          <div className="counter ms-2 mb-1">
            <small>
              {data!.count}
              {' / '}
              {data!.total_count}
            </small>
          </div>
        </div>
        <div className="d-flex">
          <Button
            color="primary"
            className="me-2"
            outline
            onClick={modalUnificaActions.toggle}
          >
            Unifica
          </Button>
          <Link className="btn btn-primary" to="/registi/new">
            <i className="bi bi-plus-circle me-2"></i> Aggiungi
          </Link>
        </div>
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className="flex-1">
        <thead>
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
                page: 1,
              })
            }
          >
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Nome
                  <SortControl field="nome" />
                </div>
              </th>
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {data!.results.map((regista) => (
            <tr key={regista.id}>
              <td>{regista.id}</td>
              <td>
                <Link to={`/registi/${regista.id}`}>{regista.nome}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare il regista
              {modalRm.value.nome} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteRegista.isLoading}
            onClick={async () => {
              await deleteRegista.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <Modal
        centered
        isOpen={modalUnifica.isOpen}
        toggle={modalUnificaActions.toggle}
      >
        <ModalHeader toggle={modalUnificaActions.toggle}>Unifica</ModalHeader>
        <Formik
          initialValues={{
            regista_to_keep: null,
            registi_to_delete: [],
          }}
          onSubmit={async (values) => {
            if (
              !values.regista_to_keep ||
              values.registi_to_delete.length === 0
            )
              return
            await unificaRegisti
              .mutateAsync({
                regista_to_keep: values.regista_to_keep,
                registi_to_delete: values.registi_to_delete,
              })
              .then(() => {
                modalUnificaActions.toggle()
              })
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <div>
                  <Field
                    component={ReactSelectField}
                    name="regista_to_keep"
                    label="Regista da mantenere"
                    placeholder="Seleziona il regista da mantenere"
                    options={registiOptions}
                  />
                </div>
                <div>
                  <Field
                    component={MultiSelectField}
                    name="registi_to_delete"
                    placeholder="Seleziona i registi da eliminare"
                    label="Registi da eliminare"
                    options={registiOptions.filter(
                      (option) => option.value !== values.regista_to_keep
                    )}
                    isMulti
                  />
                </div>
              </ModalBody>
              <ModalFooter className="d-flex justify-content-between">
                <button
                  onClick={modalUnificaActions.toggle}
                  className="btn btn-outline-primary"
                >
                  Annulla
                </button>
                <button className="btn btn-success" type="submit">
                  <i className="bi bi-save-fill me-1"></i>
                  Salva
                </button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
    </Page>
  )
}
